import {
  type AddBusiness,
  BusinessListSchema,
  BusinessResponseSchema,
  GstinLookupSchema,
  PanRegistrationsSchema,
} from '@oneapp/shared';
import { z } from 'zod';
import { api } from '../lib/api';

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

/** The business API (see packages/shared/src/api.ts). Every call is made as the signed-in person. */
export const businessApi = {
  /** A GSTIN's details from the GST register (kept for a day on the server). */
  lookup: (gstin: string, signal?: AbortSignal) =>
    api.get(`/api/v1/gstin/${encodeURIComponent(gstin)}`, GstinLookupSchema, { auth: true, ...withSignal(signal) }),

  pan: (pan: string, signal?: AbortSignal) =>
    api.get(`/api/v1/pan/${encodeURIComponent(pan)}/gstins`, PanRegistrationsSchema, {
      auth: true,
      ...withSignal(signal),
    }),

  /** A fresh Idempotency-Key per tap, so a retried request adds it once. */
  add: (input: AddBusiness, idempotencyKey: string) =>
    api.post('/api/v1/businesses', BusinessResponseSchema, { auth: true, body: input, idempotencyKey }),

  list: (signal?: AbortSignal) =>
    api.get('/api/v1/businesses', BusinessListSchema, { auth: true, ...withSignal(signal) }),

  get: (id: string, signal?: AbortSignal) =>
    api.get(`/api/v1/businesses/${encodeURIComponent(id)}`, BusinessResponseSchema, {
      auth: true,
      ...withSignal(signal),
    }),

  rename: (id: string, nickname: string | null) =>
    api.patch(`/api/v1/businesses/${encodeURIComponent(id)}`, BusinessResponseSchema, {
      auth: true,
      body: { nickname },
    }),

  remove: (id: string) => api.delete(`/api/v1/businesses/${encodeURIComponent(id)}`, z.null(), { auth: true }),
};

/** Query keys, so every screen showing businesses refreshes together. */
export const businessKeys = {
  all: ['businesses'] as const,
  one: (id: string) => ['businesses', id] as const,
  lookup: (gstin: string) => ['gstin', gstin] as const,
  pan: (pan: string) => ['pan', pan] as const,
};
